<template>
  <section class="Page" :style="{ height: pageHeight }">
    <Header class="Page__header" :showServiceMenu="false" />
    <ScrollBox class="Page__scrollBox" always>
      <main ref="mainRef" class="Page__main">
        <GlobalNavigation
          class="Page__globalNavigation"
          :globalMenuItems="globalMenuItems"
        />

        <header class="Page__mainHeader">
          <Heading type="quaternary" level="2" :heading="headingTitle" />
        </header>
        <Skeleton v-if="getAdminDataLoading" />
        <div v-else ref="innerRef" class="Page__inner">
          <UserCard
            class="Page__userCard"
            :active="active"
            :invited="invited"
            :expired="expired"
          />
          <ul class="Page__licenses">
            <li
              v-for="service in serviceData"
              :key="service.serviceId"
              class="Page__licensesItem"
            >
              <ExpandItem
                v-if="service.licenses.length > 0"
                :openText="getServiceName(service.serviceId)"
                :isOpen="true"
              >
                <LicenseDisplay
                  :serviceId="service.serviceId"
                  :licenseList="service.licenses"
                />
              </ExpandItem>
            </li>
          </ul>
        </div>
      </main>
    </ScrollBox>
  </section>
</template>

<script lang="ts">
export default {
  name: 'ManagementSystem',
};
</script>

<script lang="ts" setup>
import Heading from '@spectee/components/Atoms/Heading/index.vue';
import GlobalNavigation from '@spectee/components/Organisms/GlobalNavigation/index.vue';
import { globalMenuItems } from '../data/SideMenuItems';
import Header from '@spectee/components/Organisms/Header/index.vue';
import UserCard from '@admin/components/Organisms/UserCard/index.vue';
import ScrollBox from '@spectee/components/Atoms/ScrollBox/index.vue';
import ExpandItem from '@spectee/components/Molecules/ExpandItem/index.vue';
import Skeleton from './skeleton.vue';
import { serviceMasterData } from '@spectee/resources/serviceMaster';
import { useGetAdminData } from '@spectee/graphql/queries/getAdminData';
import { ServiceData } from '@spectee/graphql/api';
import { ref, onMounted, onUnmounted, nextTick, watch } from 'vue';
import { useWindowHeight } from '@spectee/composables/useWindowHeight';
import { useWindowWidth } from '@spectee/composables/useWindowWidth';
import messages from '../messages.json';
import { isNotNullish } from '@spectee/ts/isNotNullish';

const headingTitle = ref(messages.home.orgName);
const defaultCount = '00';
const active = ref<string | number>(defaultCount);
const invited = ref<string | number>(defaultCount);
const expired = ref<string | number>(defaultCount);
const serviceData = ref<ServiceData[]>([]);

const { useGetAdminDataRes, loading: getAdminDataLoading } = useGetAdminData();

const getServiceName = (id: string) => {
  return serviceMasterData.find(service => service.id === id)?.name;
};
watch(
  () => useGetAdminDataRes.value,
  newValue => {
    active.value = newValue?.activeUserCount || defaultCount;
    expired.value = newValue?.expiredUserCount || defaultCount;
    invited.value = newValue?.invitedUserCount || defaultCount;
    serviceData.value = (newValue?.servicesData ?? []).filter(isNotNullish);
  }
);

const { pageHeight } = useWindowHeight();
const { isSmallPixel } = useWindowWidth();

const mainRef = ref<HTMLElement | null>(null);
const innerRef = ref<HTMLElement | null>(null);

const setContentsHeight = async () => {
  if (mainRef.value && innerRef.value) {
    innerRef.value.style.height = '';
    await nextTick();

    const mainStyle = getComputedStyle(mainRef.value);
    const mainHeight = parseFloat(mainStyle.height);
    const headerHeight = 24;
    const pageContentHeight = mainHeight - headerHeight;

    if (!isSmallPixel.value) {
      innerRef.value.style.height = `${pageContentHeight}px`;
    }
  }
};

onMounted(() => {
  setContentsHeight();
  window.addEventListener('resize', setContentsHeight);
});

onUnmounted(() => {
  window.removeEventListener('resize', setContentsHeight);
});
</script>

<style lang="scss" scoped>
@use '@spectee/assets/scss/_pageForward.scss' as *;

$margin-horizontal: $service-unit * 6;

.Page {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-width: $service-unit * 62;

  & &__header {
    position: unset;
  }

  &__mainHeader,
  & &__newsCard,
  & &__userCard,
  & &__alertCenterCard,
  &__cardWrapper,
  & &__contactCard {
    margin-top: $service-unit * 4;
  }

  &__main {
    flex-grow: 1;
    padding-top: $service-unit * 2;
    padding-right: $service-unit * 8;
    padding-bottom: $service-unit * 8;
    padding-left: $service-unit * 68;
  }

  &__licenses {
    margin-bottom: $service-unit * 4;
  }

  &__licensesItem {
    margin-top: $service-unit * 4;
  }

  & &__scrollBox {
    flex: 1;
    overflow: hidden;
  }

  &__inner {
    flex: 1;
    display: flex;
    flex-direction: column;
  }

  & &__userCard,
  & &__alertCenterCard,
  &__cardWrapper,
  & &__contactCard {
  }

  & &__contactCard {
    align-self: flex-start;
    padding: $service-unit * 4;
  }

  ::v-deep(.ExpandItem__inner) {
    padding: 0px !important;
  }

  @media (width: 768px) {
    &__main {
      padding-left: $service-unit * 65;
    }
  }

  @media (max-width: 767px) {
    &__main {
      position: relative;
      height: 100%;
      padding: $service-unit * 4 $service-unit * 8 $service-unit * 8
        $service-unit * 11;
    }
    &__inner {
      flex: 1;
      display: flex;
      flex-flow: column;
      min-width: $service-min-width;
    }
  }
}
</style>
